/**
 * Email Sharing Utility
 * 
 * Centralized email sharing functionality extracted from App.jsx
 * Supports EmailJS direct sending and email service deeplinks
 */

import emailjs from '@emailjs/browser';
import { logger } from './logger';
import { TIMEOUTS } from '../constants/timing';
import { SCORING, PROGRESS_THRESHOLDS } from '../constants/gameplay';

// EmailJS Configuration
const EMAILJS_CONFIG = {
  SERVICE_ID: 'service_gb588mf',
  TEMPLATE_ID: 'template_3rem6dk',
  PUBLIC_KEY: '26x_rZp9m4ZPVymvg',
  FROM_NAME: 'Emmy\'s Learning Adventure',
  STUDENT_NAME: 'Emmy'
};

/**
 * Send email directly using EmailJS
 * @param {Object} options - Email sending options
 * @param {string} options.toEmail - Recipient email address
 * @param {string} options.subject - Email subject
 * @param {string} options.body - Email body content
 * @returns {Promise<Object>} EmailJS result
 */
export const sendDirectEmail = async ({ toEmail, subject, body }) => {
  try {
    const templateParams = {
      to_email: toEmail,
      subject,
      message: body,
      from_name: EMAILJS_CONFIG.FROM_NAME,
      student_name: EMAILJS_CONFIG.STUDENT_NAME,
      subject_line: subject, // Additional parameter to ensure subject is captured
      email_body: body // Additional parameter to ensure body is captured
    };
    
    logger.debug('Sending email with params:', {
      serviceId: EMAILJS_CONFIG.SERVICE_ID,
      templateId: EMAILJS_CONFIG.TEMPLATE_ID,
      publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
      templateParams
    });
    
    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      templateParams,
      EMAILJS_CONFIG.PUBLIC_KEY
    );
    
    return result;
  } catch (error) {
    logger.error('Email sending failed:', error);
    logger.error('Error details:', {
      status: error.status,
      text: error.text,
      message: error.message,
      fullError: error
    });
    
    // Provide helpful error messages
    if (error.text && error.text.includes('template ID not found')) {
      throw new Error('Email template not found! Please create a template called "template_emmy_learning" in your EmailJS dashboard. For now, please use Gmail, Outlook, or Yahoo options.');
    } else if (error.text && error.text.includes('Public Key is invalid')) {
      throw new Error('EmailJS Public Key is invalid. Please check your EmailJS dashboard for the correct public key.');
    } else if (error.text && error.text.includes('Service ID')) {
      throw new Error('EmailJS Service ID is invalid. Please check your EmailJS dashboard for the correct service ID.');
    } else {
      throw new Error(error.text || error.message || 'Unknown error occurred while sending email.');
    }
  }
};

/**
 * Generate email sharing URL for various email services
 * @param {Object} options - Email options
 * @param {string} options.service - Service type: 'mailto', 'gmail', 'outlook', 'yahoo'
 * @param {string} options.toEmail - Recipient email
 * @param {string} options.subject - Email subject
 * @param {string} options.body - Email body
 * @returns {string} Sharing URL
 */
export const generateEmailShareUrl = ({ service, toEmail, subject, body }) => {
  switch (service) {
    case 'gmail':
      return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(toEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    case 'outlook':
      return `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(toEmail)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    case 'yahoo':
      return `https://compose.mail.yahoo.com/?to=${encodeURIComponent(toEmail)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    case 'mailto':
    default:
      return `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }
};

/**
 * Share module completion via email
 * @param {Object} options - Sharing options
 * @param {string} options.service - Email service ('mailto', 'gmail', 'outlook', 'yahoo', 'direct')
 * @param {string} options.toEmail - Recipient email
 * @param {string} options.subjectName - Name of the completed subject
 * @param {number} options.score - Score achieved
 * @param {number} options.questionsAnswered - Number of questions answered
 * @param {number} options.percentage - Percentage score
 * @param {string} options.shareMessage - Optional custom message
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 */
export const shareModuleCompletion = async ({
  service = 'mailto',
  toEmail,
  subjectName,
  score,
  questionsAnswered,
  percentage,
  shareMessage = ''
}) => {
  const emailSubject = `🎉 Emmy Completed ${subjectName} Module - ${percentage}% Score!`;
  const emailBody = `
Hi! 

I just completed the ${subjectName} module in Emmy's Learning Adventure! 

📊 My Results:
• Subject: ${subjectName}
• Score: ${score} points
• Percentage: ${percentage}%
• Questions Answered: ${questionsAnswered}
• Correct Answers: ${Math.floor(score / SCORING.POINTS_DIVISOR)}
• Completed: ${new Date().toLocaleString()}

${percentage >= PROGRESS_THRESHOLDS.PERCENTAGE_EXCELLENT ? "I got almost everything right! 🌟" : percentage >= PROGRESS_THRESHOLDS.PERCENTAGE_GOOD ? "I did really well! 😊" : "I'm learning and getting better! 💪"}

${shareMessage || "I'm so proud of my progress! 🎉"}

Keep encouraging my learning journey! 

Love,
Your Student ✨
  `.trim();

  if (service === 'direct') {
    try {
      await sendDirectEmail({ toEmail, subject: emailSubject, body: emailBody });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  const shareUrl = generateEmailShareUrl({
    service,
    toEmail,
    subject: emailSubject,
    body: emailBody
  });
  
  window.open(shareUrl, '_blank');
  return { success: true };
};

/**
 * Share progress update via email
 * @param {Object} options - Sharing options
 * @param {string} options.service - Email service ('mailto', 'gmail', 'outlook', 'yahoo', 'direct')
 * @param {string} options.toEmail - Recipient email
 * @param {Object} options.progress - Progress data
 * @param {number} options.progress.totalScore - Total score
 * @param {number} options.completedSubjects - Number of completed subjects
 * @param {number} options.totalSubjects - Total number of subjects
 * @param {number} options.accuracy - Overall accuracy percentage
 * @param {number} options.questionsAnswered - Total questions answered
 * @param {number} options.achievementsCount - Number of achievements earned
 * @param {number} options.learningStreak - Learning streak in days
 * @param {Object} options.favoriteSubject - Favorite subject object (optional)
 * @param {string} options.shareMessage - Optional custom message
 */
export const shareProgressUpdate = async ({
  service = 'mailto',
  toEmail,
  progress,
  completedSubjects,
  totalSubjects,
  accuracy,
  questionsAnswered,
  achievementsCount,
  learningStreak,
  favoriteSubject = null,
  shareMessage = ''
}) => {
  const emailSubject = `📚 Emmy's Learning Progress Update - ${completedSubjects}/${totalSubjects} Subjects Complete!`;
  const emailBody = `
Hi! 

I wanted to share my amazing learning progress in Emmy's Learning Adventure! 

📊 My Overall Progress:
• Total Points: ${progress.totalScore}
• Subjects Completed: ${completedSubjects}/${totalSubjects}
• Overall Accuracy: ${accuracy}%
• Questions Answered: ${questionsAnswered}
• Achievements Earned: ${achievementsCount}
• Learning Streak: ${learningStreak} days
${favoriteSubject ? `• Favorite Subject: ${favoriteSubject.name}` : ''}

${completedSubjects >= PROGRESS_THRESHOLDS.SUBJECTS_EXCELLENT ? "I'm almost done with all subjects! 🎓" : completedSubjects >= PROGRESS_THRESHOLDS.SUBJECTS_GOOD ? "I'm making great progress! 🌟" : "I'm building my learning foundation! 💪"}

${shareMessage || "I'm so proud of my learning journey! 🎉"}

Keep encouraging my education! 

Love,
Your Student ✨
  `.trim();

  if (service === 'direct') {
    try {
      await sendDirectEmail({ toEmail, subject: emailSubject, body: emailBody });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  const shareUrl = generateEmailShareUrl({
    service,
    toEmail,
    subject: emailSubject,
    body: emailBody
  });
  
  window.open(shareUrl, '_blank');
  return { success: true };
};

/**
 * Get subject display name from screen key
 * @param {string} screenKey - Screen key (e.g., 'phonics', 'math')
 * @returns {string} Display name
 */
export const getSubjectDisplayName = (screenKey) => {
  const subjectNames = {
    'phonics': 'Phonics',
    'math': 'Math',
    'reading': 'Reading',
    'spelling': 'Spelling',
    'science': 'Science',
    'social': 'Social Studies',
    'skipcounting': 'Skip Counting',
    'art': 'Art',
    'geography': 'Geography',
    'history': 'History'
  };
  return subjectNames[screenKey] || screenKey;
};

/**
 * Prepare share results data for a completed module
 * @param {Object} options - Share options
 * @param {string} options.subject - Subject key (e.g., 'phonics')
 * @param {number} options.score - Score achieved
 * @param {number} options.questionsAnswered - Number of questions answered
 * @param {number} options.correctAnswers - Number of correct answers
 * @param {string} options.shareMessage - Optional custom message
 * @param {Object} options.subjectDisplayNames - Subject display names mapping
 * @param {Function} options.calculatePercentage - Function to calculate percentage
 * @param {Function} options.getShareMessage - Function to get default share message
 * @returns {Object} Share data object
 */
export const prepareShareResults = ({
  subject,
  score,
  questionsAnswered,
  correctAnswers,
  shareMessage = '',
  subjectDisplayNames = {},
  calculatePercentage,
  getShareMessage
}) => {
  const subjectName = subjectDisplayNames[subject] || subject;
  const percentage = calculatePercentage ? calculatePercentage(score, questionsAnswered) : Math.round((score / questionsAnswered) * 100);
  const completionTime = new Date().toLocaleString();
  
  const shareData = {
    subject: subjectName,
    score: score,
    percentage: percentage,
    questionsAnswered: questionsAnswered,
    correctAnswers: correctAnswers,
    completedAt: completionTime,
    message: shareMessage || (getShareMessage ? getShareMessage(subjectName, percentage) : `I completed ${subjectName} with ${percentage}%!`)
  };

  return shareData;
};

